'use client'

import React, { useState, useEffect } from 'react'
import { Sidebar } from './components/layout/Sidebar'
import { StatusBar } from './components/layout/StatusBar'
import { TopBar } from './components/layout/TopBar'
import Dashboard from './components/Dashboard/Dashboard'
import { DeviceManager } from './components/DeviceManager'
import { DataCenter } from './components/DataCenter'
import { Visualizer } from './components/Visualizer'
import Documents from './components/Documents'
import { Settings } from './components/Settings'
import { StreamProcess } from './components/StreamProcess'
import { WelcomeScreen } from './components/WelcomeScreen'
import { useUIStore } from './stores/uiStore'
import { useSystemStore } from './stores/systemStore'
import { useStorageStore } from './stores/storageStore'
import { Toaster } from './components/ui/sonner'
import { usePWAUpdate } from './hooks/usePWAUpdate'
import { useTheme } from './hooks/useTheme'
import UpdateNotification from './components/PWA/UpdateNotification'
import DemoModeBanner from './components/DemoModeBanner'
import type { MenuId } from './stores/uiStore'
import { hashToMenu } from './utils/router'
import { createLogger } from './utils/logger'
import { Capacitor } from '@capacitor/core'

const logger = createLogger('App')

export default function App() {
  const isNative = Capacitor.isNativePlatform()
  const [showWelcome, setShowWelcome] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { activeMenu, setActiveMenu } = useUIStore()
  const { initializeSystem, isInitialized, systemStatus, systemError } = useSystemStore()
  // SDD 010: IndexedDB 단일 저장소 — initializeStorage, checkAndRestoreStorage 제거됨
  const { sessions, loadSessions } = useStorageStore();
  
  // PWA 업데이트 시스템
  const {
    showUpdateNotification,
    performUpdate,
    dismissUpdate,
    isUpdating
  } = usePWAUpdate()

  // PWA 바로가기 처리
  useEffect(() => {
    const handleShortcut = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const shortcut = urlParams.get('shortcut')
      
      if (shortcut) {
        // URL 파라미터 제거 (브라우저 히스토리 정리)
        const url = new URL(window.location.href)
        url.searchParams.delete('shortcut')
        window.history.replaceState({}, '', url.toString())
        
        // 바로가기에 따른 메뉴 설정
        let targetMenu: MenuId = 'engine' // 기본값
        
        switch (shortcut) {
          case 'home':
          case 'dashboard':
            targetMenu = 'home'
            break
          case 'datacenter':
            targetMenu = 'datacenter'
            break
          case 'visualizer':
            targetMenu = 'visualizer'
            break
          case 'device':
            targetMenu = 'linkband'
            break
          default:
            targetMenu = 'home'
        }
        
        // 메뉴 변경
        setActiveMenu(targetMenu)
        
        // 바로가기로 실행된 경우 환영 화면 스킵
        setShowWelcome(false)
        
        logger.debug(`PWA shortcut: ${shortcut} -> ${targetMenu}`)
      }
    }
    
    // 페이지 로드 시 바로가기 처리
    handleShortcut()
  }, [setActiveMenu])

  // 해시 라우팅 — URL 해시(`#/documents` 등)와 activeMenu 동기화
  useEffect(() => {
    const applyHash = () => {
      const menu = hashToMenu(window.location.hash);
      if (menu && menu !== useUIStore.getState().activeMenu) {
        setActiveMenu(menu);
      }
    };

    // 초기 로드 시 해시 반영 (직접 접근 `/#/documents` 등)
    applyHash();

    // 뒤로가기/앞으로가기, 해시 수동 변경 감지
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, [setActiveMenu])
  useEffect(() => {
    const initializeApp = async () => {
      try {
        logger.info('SystemControlService initializing...')
        await initializeSystem()
        logger.info('SystemControlService initialized')
        
        // SDD 010: IndexedDB는 브라우저가 자동 관리 — 명시적 복원 불필요
        console.log('🔧 IndexedDB 저장소 준비 완료')
      } catch (error) {
        console.error('🔧 App 초기화 실패:', error)
      }
    }

    if (!isInitialized && systemStatus === 'idle') {
      initializeApp()
    }
  }, [initializeSystem, isInitialized, systemStatus])

  // SDK/UI 분리: SDK 모듈들에 store 콜백 주입
  useEffect(() => {
    import('./utils/bluetoothService').then(({ bluetoothService }) => {
      bluetoothService.setCallbacks({
        onSensorContactChanged: (ch1: boolean, ch2: boolean) => {
          useSystemStore.getState().updateSensorContactStatus(ch1, ch2);
        },
      });
    });

    import('./services/AnalysisMetricsService').then(({ AnalysisMetricsService }) => {
      AnalysisMetricsService.getInstance().setCallbacks({
        onMetricsUpdate: () => {
          // no-op — analysis metrics currently don't need store updates
        },
      });
    });

    import('./services/DeviceStatusMonitor').then(async ({ deviceStatusMonitor }) => {
      const { useDeviceStore } = await import('./stores/deviceStore');
      deviceStatusMonitor.setCallbacks({
        getDeviceState: () => useDeviceStore.getState(),
        onDeviceStatusChange: (action, data) => {
          const store = useDeviceStore.getState();
          const fn = (store as unknown as Record<string, (arg?: unknown) => void>)[action];
          if (typeof fn === 'function') fn(data);
        },
      });
    });

    import('./utils/StreamProcessor').then(async () => {
      const { useProcessedDataStore } = await import('./stores/processedDataStore');
      // StreamProcessor 인스턴스는 SystemControlService 내부에 존재 — 그쪽에서 wiring.
      void useProcessedDataStore;
    });
  }, [])

  const renderContent = () => {
    switch (activeMenu) {
      case 'home':
      case 'engine':
        return <Dashboard />
      case 'linkband':
        return <DeviceManager />
      case 'visualizer':
        return <Visualizer />
      case 'datacenter':
        return <DataCenter />
      case 'stream-process':
        return <StreamProcess />
      case 'documents':
      case 'cloudmanager':
        return <Documents />
      case 'settings':
        return <Settings />
      default:
        return <Dashboard />
    }
  }

  // 테마 초기 적용 (data-brand 속성 + 저장값 복원)
  useTheme()

  if (showWelcome) {
    return <WelcomeScreen onComplete={() => setShowWelcome(false)} />
  }

  if (systemStatus === 'initializing') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: 'var(--lb-indigo-500)' }}></div>
          <p style={{ color: 'var(--fg-2)' }}>시스템 초기화 중...</p>
        </div>
      </div>
    )
  }

  if (systemStatus === 'error') {
    return (
      <div className="h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
        <div className="text-center">
          <p className="mb-4" style={{ color: 'var(--text-danger)' }}>시스템 초기화 실패</p>
          <p style={{ color: 'var(--fg-2)', fontSize: 14 }}>{systemError}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={isNative ? 'app app--native' : 'app'}>
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <div className="app__main">
        <TopBar activeMenu={activeMenu} onMenuToggle={() => setSidebarOpen(true)} />
        <DemoModeBanner />
        <main className="app__content" style={{ flex: 1, overflow: 'auto', paddingBottom: 56 }}>
          {renderContent()}
        </main>
      </div>
      <StatusBar />

      <UpdateNotification
        isVisible={showUpdateNotification}
        onUpdate={performUpdate}
        onDismiss={dismissUpdate}
      />

      {isUpdating && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(0,0,0,0.4)' }}>
          <div className="lb-card" style={{ padding: 24, maxWidth: 360, margin: '0 16px' }}>
            <div className="flex items-center" style={{ gap: 12 }}>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: 'var(--lb-indigo-500)' }}></div>
              <div>
                <h3 style={{ fontWeight: 600, color: 'var(--fg-1)' }}>업데이트 중</h3>
                <p style={{ fontSize: 14, color: 'var(--fg-2)' }}>잠시만 기다려 주세요...</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toaster position="top-right" />
    </div>
  )
}
